import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { GridApi } from 'ag-grid-community';
import { useComposeAppGlobalState } from '@opengov/compose-app/composeAppContext';
import { BooleanOperators, FilterOperators } from '@opengov/reporting-types/src/reportFilters';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { selectFilterValuesByColumn } from '../ReportDataTableAgGrid/selectors/selectSelectedAgGridColumn';
import { styles } from './DetailTableTextFilter.styles';

let textFilterInstanceCount = 0;

export type DetailTableTextFilterCondition = {
  operator: FilterOperators;
  text: string;
};

export type DetailTableTextFilterModel = {
  filterType: 'detailTableText';
  filters: DetailTableTextFilterCondition[];
  booleanOperator: BooleanOperators;
};

type Props = {
  // The filter currently applied to the column. With `enableFilterHandlers` ag-grid hands this
  // to the component as a prop rather than through the imperative setModel/refresh handle.
  model?: DetailTableTextFilterModel | null;
  onModelChange: (model: DetailTableTextFilterModel | null) => void;
  colDef?: { field?: string };
  [key: string]: unknown;
};

export type DetailTableTextFilterRef = {
  setModel: (model: DetailTableTextFilterModel | null) => void;
  refresh: (params: { model?: DetailTableTextFilterModel | null }) => boolean;
};

type OperatorOption = {
  value: FilterOperators;
  label: string;
};

const OPERATOR_OPTIONS: OperatorOption[] = [
  { value: FilterOperators.Equal, label: 'Equals' },
  { value: FilterOperators.NotEqual, label: 'Does not equal' },
  { value: FilterOperators.Contain, label: 'Contains' },
  { value: FilterOperators.NotContain, label: 'Does not contain' },
  { value: FilterOperators.Blank, label: 'Is empty' },
  { value: FilterOperators.NotBlank, label: 'Is not empty' },
  { value: FilterOperators.Begin, label: 'Starts with' },
  { value: FilterOperators.End, label: 'Ends with' }
];

const BOOLEAN_OPERATOR_OPTIONS = [
  { value: BooleanOperators.And, label: 'And' },
  { value: BooleanOperators.Or, label: 'Or' }
] as const;

function isEqualsOp(op: FilterOperators): boolean {
  return op === FilterOperators.Equal || op === FilterOperators.NotEqual;
}

function isBlankOp(op: FilterOperators): boolean {
  return op === FilterOperators.Blank || op === FilterOperators.NotBlank;
}

// ag-grid hands over a new model object on every render, so compare by content to tell an actual
// filter change from a re-render that would otherwise discard conditions the user is part-way
// through adding.
function serializeModel(model: DetailTableTextFilterModel | null | undefined): string {
  return JSON.stringify(model ?? null);
}

export const DetailTableTextFilter = forwardRef<DetailTableTextFilterRef, Props>((props, ref) => {
  const instanceId = useRef(`text-filter-${String(++textFilterInstanceCount)}`).current;
  const operatorLabelId = `${instanceId}-operator`;
  const valueInputId = `${instanceId}-value`;
  const columnId = props.colDef?.field ?? '';
  const { model = null } = props;

  const [conditions, setConditions] = useState<DetailTableTextFilterCondition[]>(() => model?.filters ?? []);
  const [booleanOperator, setBooleanOperator] = useState<BooleanOperators>(
    () => model?.booleanOperator ?? BooleanOperators.And
  );
  const [newOperator, setNewOperator] = useState<FilterOperators>(FilterOperators.Contain);
  const [newText, setNewText] = useState('');
  const [searchString, setSearchString] = useState('');
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [valuesLoading, setValuesLoading] = useState(true);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const syncedModel = useRef(serializeModel(model));

  const showModel = useCallback((next: DetailTableTextFilterModel | null | undefined) => {
    syncedModel.current = serializeModel(next);
    setConditions(next?.filters ?? []);
    setBooleanOperator(next?.booleanOperator ?? BooleanOperators.And);
    setFormIsDirty(false);
  }, []);

  // Read column values directly from Redux so filterParams in column defs stays stable.
  // This prevents ag-grid from detecting a column def change when search results arrive,
  // which would otherwise fire filterChanged and trigger an unnecessary server-side refresh.
  const filterValuesByColumn = useComposeAppGlobalState(selectFilterValuesByColumn);
  const availableValues: [string, string][] = columnId
    ? Array.from(filterValuesByColumn.get(columnId) ?? []).map(([val, count]) => [val, String(count)])
    : [];

  useImperativeHandle(ref, () => ({
    // Called by ag-grid when setFilterModel() is invoked programmatically (e.g., view switch).
    setModel(next: DetailTableTextFilterModel | null) {
      showModel(next);
    },
    refresh(params) {
      // Only update conditions when model is explicitly null (user cleared) or a new model object.
      // If model is undefined, ag-grid has no update for us — don't reset the displayed state.
      if (params.model !== undefined) {
        showModel(params.model);
      }
      return true;
    }
  }));

  // Under `enableFilterHandlers` the applied filter reaches this panel as a prop, so the imperative
  // handle above never fires and the Added chips have to be re-read whenever the model changes.
  // Without this, removing a condition from the global Filters section left the removed chip
  // showing here even though the table data had already refreshed (BUD-18652).
  useEffect(() => {
    if (serializeModel(model) === syncedModel.current) return;
    showModel(model);
  }, [model, showModel]);

  // Mark loading done when values arrive from Redux.
  useEffect(() => {
    if (availableValues.length > 0) {
      setValuesLoading(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }
  }, [availableValues.length]);

  // Reset loading state when switching to an equals operator.
  useEffect(() => {
    if (isEqualsOp(newOperator)) {
      if (availableValues.length > 0) {
        setValuesLoading(false);
      } else {
        setValuesLoading(true);
        // Safety fallback: stop spinner after 8s so "No matches" appears even if fetch returns empty.
        loadingTimeoutRef.current = setTimeout(() => {
          setValuesLoading(false);
        }, 8000);
      }
    }
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOperator]);

  const handleAddCondition = () => {
    if (!newText.trim()) return;
    setConditions((prev) => [...prev, { operator: newOperator, text: newText }]);
    setNewText('');
    setFormIsDirty(true);
  };

  const handleAddBlankCondition = () => {
    setConditions((prev) => [...prev, { operator: newOperator, text: '' }]);
    setFormIsDirty(true);
  };

  const handleToggleValue = (value: string) => {
    const isChecked = conditions.some((c) => c.operator === newOperator && c.text === value);
    if (isChecked) {
      setConditions((prev) => prev.filter((c) => !(c.operator === newOperator && c.text === value)));
    } else {
      setConditions((prev) => [...prev, { operator: newOperator, text: value }]);
    }
    setFormIsDirty(true);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
    setFormIsDirty(true);
  };

  const handleApply = () => {
    const nextModel: DetailTableTextFilterModel = {
      filterType: 'detailTableText',
      filters: conditions,
      booleanOperator
    };
    syncedModel.current = serializeModel(nextModel);
    props.onModelChange(nextModel);
    setFormIsDirty(false);
    (props.api as GridApi | undefined)?.hideColumnFilter();
  };

  const handleClear = () => {
    showModel(null);
    setNewText('');
    setSearchString('');
    props.onModelChange(null);
    (props.api as GridApi | undefined)?.hideColumnFilter();
  };

  const filteredValues = availableValues.filter(([value]) => value.toLowerCase().includes(searchString.toLowerCase()));

  let checkboxContent: React.ReactNode;
  if (valuesLoading) {
    checkboxContent = (
      <Box sx={styles.checkboxCenteredContent}>
        <CircularProgress size={24} aria-label="Loading filter values" />
      </Box>
    );
  } else if (filteredValues.length === 0) {
    checkboxContent = (
      <Box sx={styles.checkboxCenteredContent}>
        <Typography component="span" variant="bodySmall" sx={styles.noMatchesText}>
          No matches found
        </Typography>
      </Box>
    );
  } else {
    checkboxContent = filteredValues.map(([value, count]) => (
      <Box key={value} sx={styles.checkboxRow} data-test={`value-option-${value}`}>
        <FormControlLabel
          sx={styles.formControlLabel}
          control={
            <Checkbox
              checked={conditions.some((c) => c.operator === newOperator && c.text === value)}
              onChange={() => {
                handleToggleValue(value);
              }}
              size="small"
              sx={styles.checkbox}
            />
          }
          label={value}
        />
        <Typography component="span" variant="bodySmall" sx={styles.valueCount}>
          ({count})
        </Typography>
      </Box>
    ));
  }

  return (
    <Box ref={panelRef} sx={styles.panel}>
      <Typography component="span" variant="bodySmall" sx={styles.sectionLabel}>
        Add Filter
      </Typography>

      <Box sx={styles.operatorRow}>
        <Typography id={operatorLabelId} component="span" variant="bodySmall" sx={styles.inlineLabel}>
          Operator
        </Typography>
        <Box sx={styles.inputWithButton}>
          <Select
            value={newOperator}
            onChange={(e) => {
              setNewOperator(e.target.value as FilterOperators);
            }}
            size="small"
            labelId={operatorLabelId}
            MenuProps={{ disablePortal: true }}
            SelectDisplayProps={{ 'data-test': 'operator-select' } as React.HTMLAttributes<HTMLDivElement>}
            sx={styles.operatorSelect}
          >
            {OPERATOR_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {isBlankOp(newOperator) && (
            <Button onClick={handleAddBlankCondition} size="small" variant="outlined" data-test="add-blank-button">
              Add
            </Button>
          )}
        </Box>
      </Box>

      {!isBlankOp(newOperator) && (
        <Box sx={styles.inputRow}>
          <Typography component="label" htmlFor={valueInputId} variant="bodySmall" sx={styles.inlineLabel}>
            {isEqualsOp(newOperator) ? 'Search' : 'Value'}
          </Typography>
          {isEqualsOp(newOperator) ? (
            <TextField
              value={searchString}
              onChange={(e) => {
                setSearchString(e.target.value);
              }}
              size="small"
              sx={styles.valueInput}
              inputProps={{ id: valueInputId, 'data-test': 'value-search-input' }}
            />
          ) : (
            <Box sx={styles.inputWithButton}>
              <TextField
                value={newText}
                onChange={(e) => {
                  setNewText(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddCondition();
                }}
                size="small"
                sx={styles.valueInput}
                inputProps={{ id: valueInputId, 'data-test': 'text-input' }}
              />
              <Button
                onClick={handleAddCondition}
                disabled={!newText.trim()}
                size="small"
                variant="outlined"
                sx={styles.addButton}
                data-test="add-button"
              >
                Add
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isEqualsOp(newOperator) && (
        <Box sx={styles.checkboxList} aria-live="polite" aria-busy={valuesLoading}>
          {checkboxContent}
        </Box>
      )}

      {conditions.length > 0 && (
        <>
          <Box sx={styles.addedSection}>
            <Typography component="span" variant="bodySmall" sx={styles.sectionLabel}>
              Added
            </Typography>
            <Box sx={styles.logicSection}>
              <Typography id={`${instanceId}-mode`} component="span" variant="bodySmall" sx={styles.inlineLabel}>
                Mode
              </Typography>
              <RadioGroup
                row
                value={booleanOperator}
                onChange={(e) => {
                  setBooleanOperator(e.target.value as BooleanOperators);
                  setFormIsDirty(true);
                }}
                aria-labelledby={`${instanceId}-mode`}
                sx={styles.radioGroup}
                data-test="boolean-operator-select"
              >
                {BOOLEAN_OPERATOR_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="medium" sx={styles.radioButton} />}
                    label={opt.label}
                    sx={styles.radioFormLabel}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Box>
          <Box sx={styles.conditionList}>
            {conditions.map((condition, index) => {
              const operatorLabel =
                OPERATOR_OPTIONS.find((o) => o.value === condition.operator)?.label ?? condition.operator;
              const chipLabel = isBlankOp(condition.operator) ? operatorLabel : `${operatorLabel} "${condition.text}"`;
              const nonFirstPrefix = booleanOperator === BooleanOperators.And ? 'And' : 'Or';
              const rowPrefix = index === 0 ? 'If' : nonFirstPrefix;

              return (
                <Box key={`${index}-${condition.operator}-${condition.text}`} sx={styles.conditionRow}>
                  <Typography component="span" variant="bodySmall">
                    {rowPrefix}
                  </Typography>
                  <Tooltip
                    title={chipLabel}
                    placement="top"
                    enterDelay={1200}
                    enterNextDelay={1200}
                    componentsProps={{ tooltip: { sx: { wordBreak: 'break-word' } } }}
                  >
                    <Chip
                      label={chipLabel}
                      onDelete={() => {
                        handleRemoveCondition(index);
                      }}
                      size="small"
                      sx={styles.conditionChip}
                      data-test={`condition-chip-${String(index)}`}
                      deleteIcon={
                        <CloseIcon
                          fontSize="small"
                          aria-label={`Remove condition ${String(index + 1)}`}
                          data-test={`remove-condition-${String(index)}`}
                        />
                      }
                    />
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Box sx={styles.footer}>
        <Button onClick={handleClear} size="small" variant="outlined" data-test="clear-button">
          Clear Filters
        </Button>
        <Button onClick={handleApply} disabled={!formIsDirty} size="small" variant="contained" data-test="apply-button">
          Apply
        </Button>
      </Box>
    </Box>
  );
});

DetailTableTextFilter.displayName = 'DetailTableTextFilter';
